import { useCallback, useEffect, useState } from "react";
import {
  readRouteConfig,
  writeRouteConfig,
  ROUTE_CONFIG_CHANGE_EVENT,
  ROUTE_CONFIG_STORAGE_KEY,
  type RouteConfigMap,
} from "./routeConfig";

/**
 * Live view of the manually-measured route distances.
 *
 * Re-reads on the in-tab change event (Settings edited while Run Kilometres
 * sits open behind it) and on the cross-tab `storage` event, so an operator
 * editing distances in one tab sees the other tab's totals move.
 */
export function useRouteConfig(): [RouteConfigMap, (next: RouteConfigMap) => void] {
  const [config, setConfig] = useState<RouteConfigMap>(readRouteConfig);

  useEffect(() => {
    const reread = () => setConfig(readRouteConfig());
    const onStorage = (e: StorageEvent) => {
      if (e.key === ROUTE_CONFIG_STORAGE_KEY) reread();
    };
    window.addEventListener(ROUTE_CONFIG_CHANGE_EVENT, reread);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(ROUTE_CONFIG_CHANGE_EVENT, reread);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const save = useCallback((next: RouteConfigMap) => {
    setConfig(next);
    writeRouteConfig(next);
  }, []);

  return [config, save];
}
